import { useEffect, useMemo, useRef, useState } from "react";
import { fetchGetResponse } from "../../../utilities/ApiUtilities";
import { normalizeChatSuggestions } from "../../../utilities/chatSuggestionUtilities";
import {
  getAuthenticatedWebSocketUrl,
  getDemoWebSocketUrl,
  getToken,
  getUserId,
} from "../../../utilities/authHelper";
import { getRawTickerFromCatalogValue } from "../../../utilities/marketSymbols";
import {
  formatChartAxisTime,
  formatUserTimeWithZone,
  parseUtcTimestampToMs,
} from "./timezone";
import {
  filterForwardPointsByTenors,
  getCanonicalForwardPoints,
  getCanonicalForwardTenors,
} from "./forwardTenors";
import { getStoredCoTraderLanguage } from "./languages";
import {
  FX_ASSET_TYPE,
  fetchTickerUniverse,
  getFxRuntimeSymbol,
} from "../TickerUniverseOptions";

const clampNormalizedScore = (value) => {
  if (value == null || value === "") return null;

  const parsedValue = Number.parseFloat(String(value));
  if (!Number.isFinite(parsedValue)) return null;

  return Math.max(-1, Math.min(1, parsedValue));
};

const clampExecutionScore = (value) => {
  if (value == null || value === "") return null;

  const parsedValue = Number.parseFloat(String(value));
  if (!Number.isFinite(parsedValue)) return null;

  const normalizedValue =
    parsedValue > 1 && parsedValue <= 100 ? parsedValue / 100 : parsedValue;

  return Math.max(0, Math.min(1, normalizedValue));
};

const clampExecutionConfidence = (value) => {
  if (value == null || value === "") return null;

  const parsedValue = Number.parseFloat(String(value));
  if (!Number.isFinite(parsedValue)) return null;

  const normalizedValue = parsedValue <= 1 ? parsedValue * 100 : parsedValue;

  return Math.max(0, Math.min(100, Math.round(normalizedValue)));
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeExecutionExplanation = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value)
      .map(([title, detail]) => [
        String(title || "").trim(),
        stripHtml(detail),
      ])
      .filter(([title, detail]) => title && detail),
  );
};

export const normalizeFxExecutionDecisionMessage = (rawMessage) => {
  let payload = rawMessage;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return {
        decision: null,
        explanation: {},
      };
    }
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      decision: null,
      explanation: {},
    };
  }

  const rawDecision =
    payload.decision &&
    typeof payload.decision === "object" &&
    !Array.isArray(payload.decision)
      ? payload.decision
      : {};

  let algoScore = clampExecutionScore(rawDecision.algo);
  let rfqScore = clampExecutionScore(rawDecision.rfq);

  if (algoScore == null && rfqScore != null) {
    algoScore = Math.max(0, Math.min(1, 1 - rfqScore));
  }
  if (rfqScore == null && algoScore != null) {
    rfqScore = Math.max(0, Math.min(1, 1 - algoScore));
  }

  if (algoScore != null && rfqScore != null) {
    const totalScore = algoScore + rfqScore;
    if (totalScore > 0) {
      algoScore = algoScore / totalScore;
      rfqScore = rfqScore / totalScore;
    }
  }

  let recommended = String(rawDecision.recommended || "")
    .trim()
    .toLowerCase();
  if (recommended !== "algo" && recommended !== "rfq") {
    recommended = (algoScore ?? 0.5) >= (rfqScore ?? 0.5) ? "algo" : "rfq";
  }

  const confidence = clampExecutionConfidence(rawDecision.confidence);

  return {
    decision: {
      recommended,
      algo: algoScore,
      rfq: rfqScore,
      confidence,
    },
    explanation: normalizeExecutionExplanation(payload.explanation),
  };
};

const parseStreamCurrentPrice = (rawPrice) => {
  if (rawPrice == null) return null;

  if (typeof rawPrice === "object") {
    return rawPrice;
  }

  if (typeof rawPrice === "number") {
    return Number.isFinite(rawPrice)
      ? {
          current_price: rawPrice,
          spot_midpoint: rawPrice,
          spot_ask: rawPrice,
          spot_bid: rawPrice,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  if (typeof rawPrice !== "string") return null;

  try {
    const normalizedValue = rawPrice
      .replace(/'/g, '"')
      .replace(/Decimal\("([^"]+)"\)/g, "$1");
    const parsedValue = JSON.parse(normalizedValue);

    if (typeof parsedValue === "number") {
      return Number.isFinite(parsedValue)
        ? {
            current_price: parsedValue,
            spot_midpoint: parsedValue,
            spot_ask: parsedValue,
            spot_bid: parsedValue,
            timestamp: new Date().toISOString(),
          }
        : null;
    }

    if (typeof parsedValue === "object" && parsedValue !== null) {
      return parsedValue;
    }
  } catch {
    const fallbackValue = Number(rawPrice);
    return Number.isFinite(fallbackValue)
      ? {
          current_price: fallbackValue,
          spot_midpoint: fallbackValue,
          spot_ask: fallbackValue,
          spot_bid: fallbackValue,
          timestamp: new Date().toISOString(),
        }
      : null;
  }

  return null;
};

const normalizeStreamSymbol = (value) =>
  getRawTickerFromCatalogValue(value)
    .trim()
    .replace(/=x$/i, "")
    .toLowerCase();

export const useLiveTickerQuote = (
  symbol,
  { enabled = true, isDemoMode = false } = {},
) => {
  const [quote, setQuote] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const chatId = useMemo(() => {
    if (!symbol) return null;
    return `PRICE:${getRawTickerFromCatalogValue(symbol)},`;
  }, [symbol]);

  useEffect(() => {
    setQuote(null);
    setUpdatedAt(null);
  }, [symbol]);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      clearReconnectTimer();
      try {
        wsRef.current?.close();
      } catch (_) {}
      wsRef.current = null;
    };

    if (!enabled || !symbol || !chatId) {
      closeSocket();
      return () => {
        closeSocket();
      };
    }

    let didUnmount = false;
    const userId = "ticker-quote";
    const targetSymbol = normalizeStreamSymbol(symbol);

    const scheduleReconnect = () => {
      clearReconnectTimer();
      reconnectTimerRef.current = setTimeout(() => {
        if (!didUnmount) {
          void openSocket().catch((error) => {
            console.error("Live ticker quote websocket auth/setup error:", error);
          });
        }
      }, 1000);
    };

    const openSocket = async () => {
      closeSocket();

      const wsUrl = isDemoMode
        ? getDemoWebSocketUrl(chatId)
        : await getAuthenticatedWebSocketUrl(chatId, userId);
      if (didUnmount) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const messageSymbol = normalizeStreamSymbol(
            data.symbol || data.currency_pair || data.ticker
          );

          if (messageSymbol && messageSymbol !== targetSymbol) {
            return;
          }

          const raw = data.raw || {};
          const parsedCurrentPrice =
            parseStreamCurrentPrice(data.current_price) ||
            parseStreamCurrentPrice(raw.current_price);

          const fallbackPrice = Number(
            [
              data.price,
              data.last,
              data.ltp,
              data.close,
              data.value,
              raw.price,
              raw.last,
              raw.ltp,
              raw.close,
            ].find((value) => value != null)
          );

          if (!parsedCurrentPrice && !Number.isFinite(fallbackPrice)) {
            return;
          }

          const nextQuote = parsedCurrentPrice
            ? {
                ...parsedCurrentPrice,
                current_price:
                  parsedCurrentPrice.spot_midpoint ??
                  parsedCurrentPrice.current_price ??
                  parsedCurrentPrice.spot_ask ??
                  parsedCurrentPrice.spot_bid,
              }
            : {
                current_price: fallbackPrice,
                spot_midpoint: fallbackPrice,
                spot_ask: fallbackPrice,
                spot_bid: fallbackPrice,
                timestamp: new Date().toISOString(),
              };

          setQuote(nextQuote);
          setUpdatedAt(Date.now());
        } catch (error) {
          console.error("Live ticker quote websocket parse error:", error);
        }
      };

      ws.onerror = (error) => {
        console.error("Live ticker quote websocket error:", error);
      };

      ws.onclose = () => {
        if (!didUnmount) {
          scheduleReconnect();
        }
      };
    };

    void openSocket().catch((error) => {
      console.error("Live ticker quote websocket auth/setup error:", error);
    });

    return () => {
      didUnmount = true;
      closeSocket();
    };
  }, [chatId, enabled, isDemoMode, symbol]);

  return {
    quote,
    updatedAt,
  };
};

const isPlainFxPairSymbol = (value) => /^[A-Z]{6}$/i.test(String(value || "").trim());

export const getYahooHistorySymbol = (symbol) => {
  const rawSymbol = getRawTickerFromCatalogValue(symbol);
  if (!rawSymbol) return "";
  if (/=X$/i.test(rawSymbol)) return rawSymbol;

  if (isPlainFxPairSymbol(rawSymbol)) {
    const normalizedSymbol = rawSymbol.toUpperCase();
    const baseCurrency = normalizedSymbol.slice(0, 3);
    const quoteCurrency = normalizedSymbol.slice(3);

    return baseCurrency === "USD"
      ? `${quoteCurrency}=X`
      : `${normalizedSymbol}=X`;
  }

  return rawSymbol;
};

const getYahooRangeParams = ({ startDate, endDate }) => {
  const startMs = parseUtcTimestampToMs(startDate);
  const endMs = parseUtcTimestampToMs(endDate);
  const nowMs = Date.now();
  const rangeMs =
    Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs
      ? endMs - startMs
      : 24 * 60 * 60 * 1000;
  const lookbackMs = Number.isFinite(startMs)
    ? Math.max(rangeMs, Math.max(0, nowMs - startMs))
    : rangeMs;

  if (lookbackMs <= 24 * 60 * 60 * 1000) {
    return { period: "1d", interval: "1m" };
  }

  if (lookbackMs <= 5 * 24 * 60 * 60 * 1000) {
    return { period: "5d", interval: "1m" };
  }

  if (lookbackMs <= 31 * 24 * 60 * 60 * 1000) {
    return { period: "1mo", interval: "5m" };
  }

  if (lookbackMs <= 92 * 24 * 60 * 60 * 1000) {
    return { period: "3mo", interval: "15m" };
  }

  if (lookbackMs <= 180 * 24 * 60 * 60 * 1000) {
    return { period: "6mo", interval: "30m" };
  }

  return { period: "1y", interval: "1d" };
};

const normalizeYahooHistoryRecords = (records = [], { startDate, endDate } = {}) => {
  const startMs = parseUtcTimestampToMs(startDate);
  const endMs = parseUtcTimestampToMs(endDate);
  const hasStartBound = Number.isFinite(startMs);
  const hasEndBound = Number.isFinite(endMs);

  const normalizedRecords = (Array.isArray(records) ? records : [])
    .map((record) => {
      const rawTimestamp =
        record?.Datetime ??
        record?.Date ??
        record?.timestamp ??
        record?.time ??
        record?.datetime ??
        record?.date;
      const timestampMs = parseUtcTimestampToMs(rawTimestamp);
      const closePrice = Number(
        record?.Close ??
          record?.close ??
          record?.price ??
          record?.current_price ??
          record?.spot_midpoint
      );

      if (!Number.isFinite(timestampMs) || !Number.isFinite(closePrice)) {
        return null;
      }

      if (hasStartBound && timestampMs < startMs) {
        return null;
      }

      if (hasEndBound && timestampMs > endMs) {
        return null;
      }

      return {
        timestamp: new Date(timestampMs).toISOString(),
        current_price: closePrice,
        spot_midpoint: closePrice,
      };
    })
    .filter(Boolean)
    .sort(
      (left, right) =>
        parseUtcTimestampToMs(left.timestamp) - parseUtcTimestampToMs(right.timestamp)
    );

  if (!hasStartBound && !hasEndBound) {
    return normalizedRecords;
  }

  const boundedRecords = normalizedRecords.filter((record) => {
    const timestampMs = parseUtcTimestampToMs(record.timestamp);
    if (hasStartBound && timestampMs < startMs) {
      return false;
    }

    if (hasEndBound && timestampMs > endMs) {
      return false;
    }

    return true;
  });

  if (boundedRecords.length > 0) {
    return boundedRecords;
  }

  const targetMs = Number.isFinite(startMs) && Number.isFinite(endMs)
    ? startMs + (endMs - startMs) / 2
    : Number.isFinite(startMs)
    ? startMs
    : endMs;

  if (!Number.isFinite(targetMs) || normalizedRecords.length === 0) {
    return normalizedRecords;
  }

  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  normalizedRecords.forEach((record, index) => {
    const timestampMs = parseUtcTimestampToMs(record.timestamp);
    const distance = Math.abs(timestampMs - targetMs);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  const sliceStart = Math.max(0, nearestIndex - 10);
  const sliceEnd = Math.min(normalizedRecords.length, nearestIndex + 11);

  return normalizedRecords.slice(sliceStart, sliceEnd);
};

export const fetchFxSymbols = async () => {
  const tickerUniverse = await fetchTickerUniverse();
  const fxOptions = Array.isArray(tickerUniverse?.options?.[FX_ASSET_TYPE])
    ? tickerUniverse.options[FX_ASSET_TYPE]
    : [];

  return fxOptions.reduce((symbols, option) => {
    const nextSymbol = getFxRuntimeSymbol(option?.value || option?.label || "");
    if (nextSymbol && !symbols.includes(nextSymbol)) {
      symbols.push(nextSymbol);
    }
    return symbols;
  }, []);
};

export const fetchSymbols = async ({
  setSymbols,
  setIsLoading,
  instrument_type,
}) => {
  if (typeof setIsLoading === "function") {
    setIsLoading(true);
  }

  try {
    if (instrument_type && instrument_type !== "FX_SPOT") {
      if (typeof setSymbols === "function") {
        setSymbols([]);
      }
      return;
    }

    const symbols = await fetchFxSymbols();
    if (typeof setSymbols === "function") {
      setSymbols(
        symbols.map((symbol) => ({
          value: symbol,
          label: symbol,
        }))
      );
    }
  } finally {
    if (typeof setIsLoading === "function") {
      setIsLoading(false);
    }
  }
};

const getFormattedTimestamp = () => formatUserTimeWithZone();

export const fetchInsightsWithTitle = async (
  setInsights,
  currencyPair,
  selectedSymbolRef,
  instrument_type,
  setCountDownInsights,
  showCountDownInsights
) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const forward_data_flag =
      instrument_type === "FX_FORWARD" ? "&forward_data_flag=true" : "";
    const show_countdown_flag = showCountDownInsights
      ? "&show_countdown=true"
      : "";
    const user_language = `&user_language=${getStoredCoTraderLanguage()}`;
    const response = await fetch(
      `${apiUrl}/globallink/insights/get_insights?currency_pair=${currencyPair}&title=true${forward_data_flag}${show_countdown_flag}${user_language}`
    );

    if (!response.ok) throw new Error("Network response was not ok.");
    const data = await response.json();
    if (selectedSymbolRef.current === currencyPair) {
      const insights = data.results.insights.map((insight) => {
        return {
          ...insight,
          timestamp: getFormattedTimestamp(),
        };
      });
      const countDownInsights = insights.filter(
        (insight) => insight.type === "count_down"
      );
      const filteredInsights = insights.filter(
        (insight) => insight.type !== "count_down"
      );
      setInsights(filteredInsights);
      setCountDownInsights(countDownInsights);
    }
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const coTraderQuery = async (query, currency_pair, summary) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const requestBody = {
      query: query,
      currency_pair: currency_pair,
      context: summary,
    };
    const response = await fetch(`${apiUrl}/globallink/insights/query`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });
    const data = await response.json();
    return data.results;
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

const normalizeSuggestedQuestionsLanguage = (language) =>
  (language || "english").toString().trim().toLowerCase();

export const fetchFxSuggestedQuestions = async ({
  instrument,
  language = "english",
}) => {
  if (!instrument) return null;

  const params = new URLSearchParams({
    instrument,
    language: normalizeSuggestedQuestionsLanguage(language),
  });

  const response = await fetchGetResponse(
    `globallink/insights/fx/suggested_questions?${params.toString()}`
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch FX suggested questions: ${response.status}`);
  }

  const data = await response.json();
  if (!data?.success) {
    throw new Error(
      data?.errorObject?.message || "Failed to fetch FX suggested questions"
    );
  }

  return data.results;
};

export const useFxSuggestedQuestions = ({
  instrument,
  language,
  enabled = true,
}) => {
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [suggestedQuestionsMeta, setSuggestedQuestionsMeta] = useState(null);
  const [suggestedQuestionsLoading, setSuggestedQuestionsLoading] =
    useState(false);
  const [suggestedQuestionsError, setSuggestedQuestionsError] = useState(null);

  useEffect(() => {
    let isCurrent = true;

    if (!enabled || !instrument) {
      setSuggestedQuestions([]);
      setSuggestedQuestionsMeta(null);
      setSuggestedQuestionsLoading(false);
      setSuggestedQuestionsError(null);
      return () => {
        isCurrent = false;
      };
    }

    const loadSuggestedQuestions = async () => {
      setSuggestedQuestionsLoading(true);
      setSuggestedQuestionsError(null);

      try {
        const results = await fetchFxSuggestedQuestions({
          instrument,
          language,
        });

        if (!isCurrent) return;

        const questions = normalizeChatSuggestions(results?.questions);

        setSuggestedQuestions(questions);
        setSuggestedQuestionsMeta(results);
      } catch (error) {
        if (!isCurrent) return;
        console.error("Error fetching FX suggested questions:", error);
        setSuggestedQuestions([]);
        setSuggestedQuestionsMeta(null);
        setSuggestedQuestionsError(error);
      } finally {
        if (isCurrent) setSuggestedQuestionsLoading(false);
      }
    };

    loadSuggestedQuestions();

    return () => {
      isCurrent = false;
    };
  }, [enabled, instrument, language]);

  return {
    suggestedQuestions,
    suggestedQuestionsMeta,
    suggestedQuestionsLoading,
    suggestedQuestionsError,
  };
};

export const useInactivity = (timeout = 300) => {
  const [isActive, setIsActive] = useState(true);
  const inactivityTimer = useRef(null);

  const resetInactivityTimer = () => {
    setIsActive(true);
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setIsActive(false);
    }, timeout * 1000);
  };

  useEffect(() => {
    const handleActivity = () => resetInactivityTimer();

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    resetInactivityTimer();

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
      clearTimeout(inactivityTimer.current);
    };
  }, []);

  return isActive;
};

export const useFxExecutionDecision = ({
  currencyPair,
  tradeAmount = "$50 million",
  side = "buy_base",
  query = "Based on current market conditions, should I execute this order via Algo or RFQ?",
  language = "English",
  requestSeed = 0,
  enabled = true,
}) => {
  const [decision, setDecision] = useState(null);
  const [explanation, setExplanation] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusText, setStatusText] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const completedRequestKeyRef = useRef(null);

  const requestKey = useMemo(
    () =>
      [
        currencyPair || "",
        tradeAmount || "",
        side || "",
        query || "",
        language || "",
        requestSeed || 0,
      ].join("::"),
    [currencyPair, tradeAmount, side, query, language, requestSeed],
  );

  useEffect(() => {
    completedRequestKeyRef.current = null;
    setDecision(null);
    setExplanation({});
    setLoading(false);
    setError(null);
    setStatusText("");
    setUpdatedAt(null);
  }, [currencyPair, tradeAmount, side, query, language]);

  useEffect(() => {
    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      clearReconnectTimer();
      try {
        wsRef.current?.close();
      } catch (_) {}
      wsRef.current = null;
    };

    if (!enabled || !currencyPair) {
      closeSocket();
      return () => {
        closeSocket();
      };
    }

    if (completedRequestKeyRef.current === requestKey) {
      return () => {
        closeSocket();
      };
    }

    let didUnmount = false;
    let hasFinished = false;
    let reconnectEnabled = true;
    let reconnectAttempts = 0;
    let activeAssistantMessageId = null;
    const maxReconnectAttempts = 5;
    const userId = getUserId() || "EXECUTION";
    const timeZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const randomNumber = Math.floor(100000 + Math.random() * 900000);
    const chatId = `EXECUTIONGPT-${userId}-${currencyPair}-${randomNumber}`;

    const requestBody = {
      chat_id: chatId,
      currency_pair: currencyPair,
      trade_amount: tradeAmount,
      side,
      query,
      userTimeZone: timeZone,
      user_language: language,
      origin: "EXECUTIONGPT",
    };

    const failRequest = (message) => {
      if (didUnmount) return;
      setError(stripHtml(message) || "Unable to load Algo vs RFQ decision.");
      setLoading(false);
      setStatusText("");
      reconnectEnabled = false;
    };

    const finishRequest = () => {
      if (didUnmount) return;
      hasFinished = true;
      reconnectEnabled = false;
      setLoading(false);
      clearReconnectTimer();
      activeAssistantMessageId = null;
    };

    const scheduleReconnect = () => {
      if (
        didUnmount ||
        hasFinished ||
        !reconnectEnabled ||
        reconnectAttempts >= maxReconnectAttempts
      ) {
        if (!hasFinished && reconnectAttempts >= maxReconnectAttempts) {
          failRequest(
            "The Algo vs RFQ decision stream disconnected before it could finish.",
          );
        }
        return;
      }

      if (!activeAssistantMessageId) {
        failRequest(
          "The Algo vs RFQ decision stream disconnected before it could be resumed.",
        );
        return;
      }

      const delayMs = Math.min(1000 * 2 ** reconnectAttempts, 10000);
      reconnectAttempts += 1;
      reconnectTimerRef.current = setTimeout(() => {
        void openSocket(true).catch((socketError) => {
          console.error(
            "FX execution websocket reconnect auth/setup error:",
            socketError,
          );
          scheduleReconnect();
        });
      }, delayMs);
    };

    const openSocket = async (isReconnect = false) => {
      closeSocket();

      const wsUrl = await getAuthenticatedWebSocketUrl(
        chatId,
        userId,
        isReconnect ? activeAssistantMessageId : undefined,
      );
      if (didUnmount) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isReconnect) return;
        setLoading(true);
        ws.send(JSON.stringify(requestBody));
      };

      ws.onmessage = (event) => {
        if (didUnmount) return;

        let data;
        try {
          data = JSON.parse(event.data);
        } catch (parseError) {
          console.error("FX execution websocket parse error:", parseError);
          return;
        }

        activeAssistantMessageId =
          data?.message_id ||
          (data?.type === "MESSAGE_ID" && data?.message != null
            ? String(data.message)
            : activeAssistantMessageId);

        const type = data?.type;
        const message = data?.message;

        if (
          type === "FINISH" ||
          type === "DONE" ||
          type === "END" ||
          type === "COMPLETE"
        ) {
          finishRequest();
          try {
            ws.close();
          } catch (_) {}
          return;
        }

        if (type === "ERROR") {
          failRequest(String(message || "Unable to load Algo vs RFQ decision."));
          try {
            ws.close();
          } catch (_) {}
          return;
        }

        if (type === "INFO") {
          setStatusText(stripHtml(message));
          return;
        }

        if (type === "MESSAGE_ID") {
          return;
        }

        if (type !== "FX_EXECUTION_DECISION") {
          return;
        }

        const normalizedPayload = normalizeFxExecutionDecisionMessage(message);
        if (!normalizedPayload.decision) {
          failRequest("Received an invalid Algo vs RFQ decision payload.");
          try {
            ws.close();
          } catch (_) {}
          return;
        }

        completedRequestKeyRef.current = requestKey;
        setDecision(normalizedPayload.decision);
        setExplanation(normalizedPayload.explanation);
        setError(null);
        setStatusText("");
        setUpdatedAt(Date.now());
        setLoading(false);
      };

      ws.onerror = (socketError) => {
        console.error("FX execution websocket error:", socketError);
      };

      ws.onclose = () => {
        if (!didUnmount && !hasFinished && reconnectEnabled) {
          scheduleReconnect();
        }
      };
    };

    void openSocket().catch((socketError) => {
      console.error("FX execution websocket auth/setup error:", socketError);
      failRequest(socketError?.message || "Unable to open execution stream.");
    });

    return () => {
      didUnmount = true;
      closeSocket();
    };
  }, [currencyPair, enabled, language, query, requestKey, side, tradeAmount]);

  return {
    decision,
    explanation,
    loading,
    error,
    statusText,
    updatedAt,
  };
};

export const useInsights = (
  selectedSymbol,
  updateSpeed,
  selectedSymbolRef,
  instrument_type,
  showCountDownInsights,
  inactivityTimeout = 1200
) => {
  const [insights, setInsights] = useState([]);
  const [countDownInsights, setCountDownInsights] = useState([]);
  const [fetchedInsights, setFetchedInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const isActive = useInactivity(inactivityTimeout);

  useEffect(() => {
    selectedSymbolRef.current = selectedSymbol;
    setLoading(true);
    setFetchedInsights([]);
    setInsights([]);
    setCountDownInsights([]);

    const fetchData = () => {
      if (isActive) {
        fetchInsightsWithTitle(
          setFetchedInsights,
          selectedSymbol,
          selectedSymbolRef,
          instrument_type,
          setCountDownInsights,
          showCountDownInsights
        );
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, updateSpeed * 10000);

    return () => clearInterval(intervalId);
  }, [
    selectedSymbol,
    updateSpeed,
    instrument_type,
    isActive,
    showCountDownInsights,
  ]);

  useEffect(() => {
    const processInsights = () => {
      if (!isActive) return;
      const updated = fetchedInsights.splice(0, 1);
      const countDownInsight = countDownInsights.splice(0, 1);
      setInsights((prev) => {
        return [...updated, ...countDownInsight, ...prev];
      });
      setLoading(false);
    };

    const intervalId = setInterval(processInsights, updateSpeed * 1000);

    return () => clearInterval(intervalId);
  }, [fetchedInsights, updateSpeed, isActive]);

  return { insights, loading, isActive };
};

export const usePrices = (
  selectedSymbol,
  setPrices,
  inactivityTimeout = 300,
  { enabled = true } = {},
) => {

  const isActive = useInactivity(inactivityTimeout);

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }

    const fetchPrices = () => {
      if (isActive) {
        fetchFilteredPrices({
          setPricesData: setPrices,
          symbol: selectedSymbol,
          tenor: "ON",
        });
      }
    };

    fetchPrices();
    // const intervalId = setInterval(fetchPrices, 20000);

    // return () => clearInterval(intervalId);
  }, [enabled, isActive, selectedSymbol, setPrices]);

};

export const fetchFilteredPrices = async ({
  symbol,
  setPricesData,
  startDate = null,
  endDate = null,
  tenor = "ON",
  useUtcDateTimeRange = false,
  intervalOverride = null,
}) => {
  void tenor;

  if (!endDate) {
    endDate = new Date(); // current time
    endDate = new Date(endDate.getTime() + 60 * 60 * 1000).toISOString();
  }

  if (!startDate) {
    const oneHourAgo = new Date(new Date().getTime() - 60 * 60 * 1000); // 1 hour prior
    startDate = oneHourAgo.toISOString();
  }

  try {
    const historySymbol = getYahooHistorySymbol(symbol);
    const { period, interval } = getYahooRangeParams({ startDate, endDate });
    const query = new URLSearchParams({
      symbol: historySymbol,
      interval: intervalOverride || interval,
    });

    if (useUtcDateTimeRange) {
      query.set("startdatetime", new Date(startDate).toISOString());
      query.set("enddatetime", new Date(endDate).toISOString());
    } else {
      query.set("period", period);
    }

    const response = await fetchGetResponse(
      `globallink/prices/get_prices_yf?${query.toString()}`
    );

    if (!response.ok) throw new Error("Network response was not ok.");

    const data = await response.json();
    if (data?.success === false || data?.results?.error) {
      throw new Error(
        data?.errorObject?.message ||
          data?.results?.error ||
          "Unable to load Yahoo price history."
      );
    }

    const price_data = normalizeYahooHistoryRecords(data?.results?.data || [], {
      startDate,
      endDate,
    });
    setPricesData(price_data);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const formattedPrices = (prices) => {
  return prices.map((item) => {
    return [parseUtcTimestampToMs(item.timestamp), parseFloat(item.current_price)];
  });
};

export const formattedSpotAsk = (prices) => {
  return prices.map((item) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_ask),
  ]);
};

export const formattedSpotBid = (prices) => {
  return prices.map((item) => [
    parseUtcTimestampToMs(item.timestamp),
    parseFloat(item.spot_bid),
  ]);
};

export const ChartOptions = ({
  prices,
  isDark,
  showBidAsk = true,
}) => {
  const theme = isDark
    ? {
        bg: "#12172A",
        plotBg: "#12172A",
        axisText: "#A6B0CF",
        titleText: "#E8ECF8",
        grid: "rgba(35,43,74,0.65)", // #232B4A w/ opacity
        axisLine: "rgba(35,43,74,0.9)",
        tooltipBg: "rgba(15,20,36,0.95)",
        tooltipText: "#E8ECF8",
        tooltipBorder: "rgba(35,43,74,0.9)",
        crosshair: "rgba(76,125,255,0.35)",
        // series colors (dark-friendly)
        mid: "#7DA0FF",
        ask: "#3FE1A2",
        bid: "#FF6B6B",
      }
    : {
        bg: "#FFFFFF",
        plotBg: "#FFFFFF",
        axisText: "#5B6472",
        titleText: "#0B1220",
        grid: "rgba(233,236,239,0.95)",
        axisLine: "rgba(233,236,239,1)",
        tooltipBg: "rgba(0,0,0,0.85)",
        tooltipText: "#FFFFFF",
        tooltipBorder: "rgba(0,0,0,0.25)",
        crosshair: "rgba(13,110,253,0.20)",
        mid: "#0D6EFD",
        ask: "#2ED573",
        bid: "#FF6B6B",
      };

  // Build series data once
  const midData = formattedPrices(prices); // expected: [[ts, val], ...]
  const askData = formattedSpotAsk(prices);
  const bidData = formattedSpotBid(prices);

  // Safe min/max from all series values (prevents mismatches)
  const allValues = [
    ...midData.map((d) => d?.[1]),
    ...askData.map((d) => d?.[1]),
    ...bidData.map((d) => d?.[1]),
  ].filter((v) => typeof v === "number" && !Number.isNaN(v));

  const yMin = allValues.length ? Math.min(...allValues) : 0;
  const yMax = allValues.length ? Math.max(...allValues) : 100;

  const series = [
    {
      type: "line",
      name: "Spot Mid",
      data: midData,
      yAxis: 0,
      color: theme.mid,
    },
  ];

  if (showBidAsk) {
    series.push(
      {
        type: "line",
        name: "Spot Ask",
        data: askData,
        yAxis: 0,
        color: theme.ask,
        opacity: 0.9,
      },
      {
        type: "line",
        name: "Spot Bid",
        data: bidData,
        yAxis: 0,
        color: theme.bid,
        opacity: 0.9,
      }
    );
  }

  return {
    chart: {
      type: "line",
      zoomType: "x",
      backgroundColor: theme.bg,
      plotBackgroundColor: theme.plotBg,
      height: null,
      style: {
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
      },
      events: {
        load: function () {
          const chart = this;
          let lastHoveredPoints = null;

          chart.container.addEventListener("mousemove", function (e) {
            const normalized = chart.pointer.normalize(e);
            const points = chart.series
              .map((s) => s.searchPoint(normalized, true))
              .filter(Boolean);

            if (points.length > 0) lastHoveredPoints = points;
          });

          chart.container.addEventListener("mouseleave", function () {
            if (lastHoveredPoints) {
              setTimeout(() => {
                chart.tooltip.refresh(lastHoveredPoints);
              }, 100);
            }
          });
        },
      },
    },

    title: { text: "" },

    xAxis: {
      type: "datetime",
      title: { text: "Time", style: { color: theme.axisText } },
      labels: {
        style: { color: theme.axisText },
        formatter: function () {
          return formatChartAxisTime(Number(this.value));
        },
      },

      // dark-mode axis + grid polish
      gridLineWidth: 1,
      gridLineColor: theme.grid,
      lineColor: theme.axisLine,
      tickColor: theme.axisLine,

      crosshair: {
        width: 1,
        color: theme.crosshair,
      },
    },

    yAxis: [
      {
        title: { text: "Price", style: { color: theme.axisText } },
        opposite: false,
        min: yMin,
        max: yMax,

        gridLineWidth: 1,
        gridLineColor: theme.grid,
        lineColor: theme.axisLine,

        labels: { style: { color: theme.axisText } },
      },
    ],

    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      itemStyle: { color: theme.axisText },
      itemHoverStyle: { color: theme.titleText },
    },

    tooltip: {
      shared: true,
      useHTML: true,
      stickOnContact: true,
      outside: false,

      // dark-mode tooltip styling
      backgroundColor: theme.tooltipBg,
      borderColor: theme.tooltipBorder,
      style: { color: theme.tooltipText, fontSize: "0.85rem" },

      formatter: function () {
        const pts = this.points || [];
        const bidPoint = pts.find((p) => p.series.name === "Spot Bid");
        const midPoint = pts.find((p) => p.series.name === "Spot Mid");
        const askPoint = pts.find((p) => p.series.name === "Spot Ask");

        return `
          <div style="color:${theme.tooltipText}">
            <b>Timestamp:</b> ${formatUserTimeWithZone(Number(this.x))}<br/>
            <b>Spot Bid:</b> ${bidPoint ? bidPoint.y.toFixed(4) : "N/A"}<br/>
            <b>Spot Mid:</b> ${midPoint ? midPoint.y.toFixed(4) : "N/A"}<br/>
            <b>Spot Ask:</b> ${askPoint ? askPoint.y.toFixed(4) : "N/A"}<br/>
          </div>
        `;
      },
    },

    plotOptions: {
      series: {
        lineWidth: 2,
        marker: {
          enabled: false, // ✅ looks cleaner for FX streams
        },
        states: {
          hover: { lineWidthPlus: 1 },
          inactive: { opacity: 0.25 },
        },
      },
    },

    series,

    credits: { enabled: false },
  };
};

// `isLoading` is true only for the first fetch of a symbol, not the hourly refresh.
export const useForwardPoints = (selectedSymbol, setTenorList) => {
  const [forwardPoints, setForwardPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const updatePrices = () =>
      fetchForwardPoints({
        setForwardPoints: setForwardPoints,
        symbol: selectedSymbol,
        setTenorList: setTenorList,
      });

    setIsLoading(true);
    updatePrices().finally(() => {
      if (active) setIsLoading(false);
    });
    const intervalId = setInterval(updatePrices, 1000 * 60 * 60);

    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [selectedSymbol, setTenorList]);
  return { forwardPoints, isLoading };
};

const fetchForwardPoints = async ({
  setForwardPoints,
  symbol,
  setTenorList,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(
      `${apiUrl}/globallink/prices/get_latest_forward_points?symbol=${symbol}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch prices");
    }
    const data = await response.json();
    const orderedForwardPoints = getCanonicalForwardPoints(
      data?.results?.latest_tenor_prices || [],
    );
    const orderedTenorList = getCanonicalForwardTenors(
      orderedForwardPoints.map((item) => item.tenor),
    );

    setTenorList(orderedTenorList);
    setForwardPoints(orderedForwardPoints);
  } catch (error) {
    console.error("Error fetching filtered prices:", error);
  }
};

export const ForwardChartOptions = ({ forwardPoints, tenor, isDark }) => {
  const theme = isDark
    ? {
        bg: "#12172A",
        axisText: "#A6B0CF",
        titleText: "#E8ECF8",
        grid: "rgba(35,43,74,0.65)",
        axisLine: "rgba(35,43,74,0.9)",
        tooltipBg: "rgba(15,20,36,0.95)",
        tooltipText: "#E8ECF8",
        tooltipBorder: "rgba(35,43,74,0.9)",
        mid: "#7DA0FF",
        bid: "#FF6B6B",
        ask: "#3FE1A2",
        midCol: "rgba(125,160,255,0.85)",
      }
    : {
        bg: "#FFFFFF",
        axisText: "#5B6472",
        titleText: "#0B1220",
        grid: "rgba(233,236,239,0.95)",
        axisLine: "rgba(233,236,239,1)",
        tooltipBg: "rgba(0,0,0,0.85)",
        tooltipText: "#FFFFFF",
        tooltipBorder: "rgba(0,0,0,0.25)",
        mid: "#0D6EFD",
        bid: "#FF6B6B",
        ask: "#2ED573",
        midCol: "rgba(13,110,253,0.85)",
      };

  const filteredForwardPoints = filterForwardPointsByTenors(
    forwardPoints,
    tenor,
  );

  const toNum = (x) => {
    const n = parseFloat(x);
    return Number.isFinite(n) ? n : 0;
  };

  const bidArr = filteredForwardPoints.map(
    (item) => toNum(item.forward_points_bid) / 10000
  );
  const askArr = filteredForwardPoints.map(
    (item) => toNum(item.forward_points_ask) / 10000
  );
  const midArr = filteredForwardPoints.map(
    (_, i) => (bidArr[i] + askArr[i]) / 2
  );

  const allVals = [...bidArr, ...askArr, ...midArr].filter(
    (v) => typeof v === "number" && !Number.isNaN(v)
  );
  const yMin = allVals.length ? Math.min(...allVals) : 0;

  return {
    chart: {
      backgroundColor: theme.bg,
      plotBackgroundColor: theme.bg,
      type: "column",
      height: null,
      zoomType: "x",
      style: {
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial",
      },
    },

    title: { text: "" },

    xAxis: {
      categories: filteredForwardPoints.map((item) => item.tenor),
      title: { text: "Tenor", style: { color: theme.axisText } },
      labels: { style: { color: theme.axisText } },
      gridLineWidth: 0,
      lineColor: theme.axisLine,
      tickColor: theme.axisLine,
    },

    yAxis: {
      title: { text: "Forward Point", style: { color: theme.axisText } },
      min: yMin,
      gridLineWidth: 1,
      gridLineColor: theme.grid,
      labels: { style: { color: theme.axisText } },
    },

    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "top",
      itemStyle: { color: theme.axisText },
      itemHoverStyle: { color: theme.titleText },
    },

    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: theme.tooltipBg,
      borderColor: theme.tooltipBorder,
      style: { color: theme.tooltipText, fontSize: "0.85rem" },
      formatter: function () {
        const idx = this.points?.[0]?.point?.index ?? 0;
        const bid = bidArr[idx];
        const ask = askArr[idx];
        const midpoint = midArr[idx];

        const fmt = (v) => (Number.isFinite(v) ? v.toFixed(4) : "N/A");

        return `
          <div style="color:${theme.tooltipText}">
            <b>Tenor: ${this.x}</b><br/>
            <b>Bid:</b> ${fmt(bid)}<br/>
            <b>Midpoint:</b> ${fmt(midpoint)}<br/>
            <b>Ask:</b> ${fmt(ask)}
          </div>
        `;
      },
    },

    plotOptions: {
      column: {
        borderWidth: 0,
        pointPadding: 0.12,
        groupPadding: 0.18,
      },
      series: {
        states: { inactive: { opacity: 0.25 } },
      },
    },

    // Keep your structure: a mid line + 3 columns (bid/mid/ask)
    series: [
      {
        type: "line",
        name: "Mid (Line)",
        data: midArr,
        color: theme.mid,
        marker: { enabled: false },
        lineWidth: 2,
        showInLegend: false, // keep hidden like your original
        zIndex: 5,
      },
      {
        type: "column",
        name: "Bid",
        data: bidArr,
        color: theme.bid,
        opacity: 0.8,
      },
      {
        type: "column",
        name: "Mid",
        data: midArr,
        color: theme.midCol,
        opacity: 0.9,
      },
      {
        type: "column",
        name: "Ask",
        data: askArr,
        color: theme.ask,
        opacity: 0.8,
      },
    ],

    credits: { enabled: false },
  };
};



// assumes you already have this
// import { useInactivity } from "./useInactivity";


export const useInsightsWs = (
  selectedSymbol,
  updateSpeed,
  selectedSymbolRef,
  showCountDownInsights,
  language,
  inactivityTimeout = 1200,
  setPrices,
  { isDemoMode = false } = {},
) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingVisibleCount, setPendingVisibleCount] = useState(0);
  const [lastReceivedAt, setLastReceivedAt] = useState(0);
  const [sentiment, setSentiment] = useState(null);
  const [sentimentUpdatedAt, setSentimentUpdatedAt] = useState(null);

  const isActive = useInactivity(inactivityTimeout);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  // ✅ queues to "drip" items 1-by-1
  const pendingInsightsRef = useRef([]); // normal insights waiting to be shown
  const seenInsightKeysRef = useRef(new Set());

  const chatId = useMemo(() => {
    const sym = selectedSymbol || "";
    return `UPDATES:${sym}_${language}`; // e.g. UPDATES:EQ-reliance.ns
  }, [selectedSymbol, language]);

  useEffect(() => {
    selectedSymbolRef.current = selectedSymbol;
  }, [selectedSymbol, selectedSymbolRef]);

  // Reset when symbol changes
  useEffect(() => {
    setLoading(true);
    setInsights([]);
    setPendingVisibleCount(0);
    setLastReceivedAt(0);
    setSentiment(null);
    setSentimentUpdatedAt(null);
    pendingInsightsRef.current = [];
    seenInsightKeysRef.current = new Set();
  }, [selectedSymbol, language]);

  // 1) WebSocket: only ENQUEUE incoming insights (do not setInsights directly)
  useEffect(() => {
    if (!isActive || !selectedSymbol) {
      try {
        wsRef.current?.close();
      } catch (_) {}
      wsRef.current = null;
      setLoading(false);
      return;
    }

    const userId = "ticker-update";
    let didUnmount = false;

    const cleanupReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeWS = () => {
      cleanupReconnectTimer();
      try {
        wsRef.current?.close();
      } catch (_) {}
      wsRef.current = null;
    };

    const scheduleReconnect = () => {
      cleanupReconnectTimer();
      const delayMs = Math.max(1000, (updateSpeed || 1) * 1000);
      reconnectTimerRef.current = setTimeout(() => {
        if (!didUnmount && isActive) {
          void connectWS().catch((error) => {
            console.error("Insight stream websocket auth/setup error:", error);
          });
        }
      }, delayMs);
    };

    const isCountDownInsight = (type) =>
      type === "count_down" || type === "countdown";

    const getInsightKey = (insight) => {
      if (insight.source_id != null && String(insight.source_id).trim()) {
        return `source::${String(insight.source_id).trim()}`;
      }

      const contentKey = [
        insight.title,
        insight.description,
        insight.type,
        insight.start_date,
        insight.timestamp,
        insight.impact_score,
        insight.question,
      ]
        .map((value) => (value == null ? "" : String(value).trim()))
        .filter(Boolean)
        .join("::");

      return contentKey || JSON.stringify(insight);
    };

    const normalize = (arr, msgSymbol) =>
      (Array.isArray(arr) ? arr : [])
        .filter(Boolean)
        .map((it, idx) => {
          const rawType = it.type ?? "insight";
          const normalizedType =
            rawType === "countdown" ? "count_down" : rawType;

          return {
            id:
              it.id || `${Date.now()}-${idx}-${(it.title || "").slice(0, 24)}`,
            source_id: it.id ?? null,
            title: it.title ?? "",
            description: it.description ?? "",
            type: normalizedType,
            currency_pair: msgSymbol || selectedSymbolRef.current,
            received_at: Date.now(),
            start_date: it.start_date || null,
            timestamp: it.timestamp || it.timeStamp || it.published_at || null,
            impact_score: clampNormalizedScore(it.impact_score),
            question:
              typeof it.question === "string" && it.question.trim()
                ? it.question.trim()
                : null,
          };
        });

    const connectWS = async () => {
      closeWS();

      const wsUrl = isDemoMode
        ? getDemoWebSocketUrl(chatId)
        : await getAuthenticatedWebSocketUrl(chatId, userId);
      if (didUnmount) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        if (didUnmount) return;

        try {
          const data = JSON.parse(event.data);
          const nextCurrentPrice = parseStreamCurrentPrice(data.current_price);
          if (nextCurrentPrice != null && typeof setPrices === "function") {
            const price = {
              ...nextCurrentPrice,
              current_price:
                nextCurrentPrice?.spot_midpoint ??
                nextCurrentPrice?.current_price,
            };
            setPrices((x) => {
              return [...x, price];
            });
          }

          const hasSentimentField = Object.prototype.hasOwnProperty.call(
            data,
            "sentiment",
          );
          if (hasSentimentField) {
            setSentiment(clampNormalizedScore(data.sentiment));
            setSentimentUpdatedAt(Date.now());
          }

          const msgSymbol =
            data.currency_pair || data.symbol || data.currencyPair || null;

          const normalized = normalize(data.insights, msgSymbol);

          if (normalized.length) {
            const visibleInsights = normalized.filter(
              (insight) =>
                showCountDownInsights || !isCountDownInsight(insight.type),
            );
            const dedupedIncoming = visibleInsights.filter((insight) => {
              const key = getInsightKey(insight);
              if (seenInsightKeysRef.current.has(key)) {
                return false;
              }
              seenInsightKeysRef.current.add(key);
              return true;
            });
            const visibleIncomingCount = dedupedIncoming.length;

            pendingInsightsRef.current.push(...dedupedIncoming);

            if (visibleIncomingCount > 0) {
              setPendingVisibleCount((current) => current + visibleIncomingCount);
              setLastReceivedAt(Date.now());
            }
          }

          if (
            hasSentimentField ||
            nextCurrentPrice != null ||
            normalized.length > 0 ||
            Array.isArray(data.insights)
          ) {
            setLoading(false);
          }
        } catch (err) {
          console.error("[Insights] WS parse error:", err);
        }
      };

      ws.onerror = (err) => console.error("[Insights] WS error:", err);

      ws.onclose = () => {
        if (didUnmount) return;
        if (isActive) scheduleReconnect();
      };
    };

    void connectWS().catch((error) => {
      console.error("Insight stream websocket auth/setup error:", error);
    });

    return () => {
      didUnmount = true;
      closeWS();
    };
  }, [
    chatId,
    isActive,
    isDemoMode,
    selectedSymbol,
    selectedSymbolRef,
    showCountDownInsights,
    updateSpeed,
    setPrices,
  ]);

  // 2) Drip processor: every updateSpeed seconds, add 1 item into UI
  useEffect(() => {
    if (!isActive) return;

    const intervalMs = Math.max(250, (updateSpeed || 1) * 1000);

    const processOne = () => {
      if (!isActive) return;

      if (pendingInsightsRef.current.length > 0) {
        const next = pendingInsightsRef.current.shift();
        if (next) {
          // A WS batch is dripped out one item per tick, so stamp the moment
          // it enters the feed (drives the "Just now" / "10s ago" labels, as
          // in GlobalLink pre-trade). The server value stays in
          // `source_timestamp`; `received_at` keeps the raw receipt time.
          const released = {
            ...next,
            source_timestamp: next.timestamp,
            timestamp: new Date().toISOString(),
          };
          setInsights((prev) => [released, ...prev]);
          setPendingVisibleCount((current) => Math.max(0, current - 1));
          setLoading(false);
        }
      }
    };

    processOne(); // optional: show first one immediately
    const id = setInterval(processOne, intervalMs);

    return () => clearInterval(id);
  }, [updateSpeed, isActive, showCountDownInsights]);

  return {
    insights,
    loading,
    isActive,
    hasPendingIncoming: pendingVisibleCount > 0,
    lastReceivedAt,
    sentiment,
    sentimentUpdatedAt,
  };
};
